import { AsyncPipe } from '@angular/common';
import { Component } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { NavigationEnd, Router } from '@angular/router';

import { filter, Observable } from 'rxjs';

import { AdobeAnalyticsService } from 'src/app/modules/core/services/adobe-analytics.service';
import { BrandService } from 'src/app/modules/core/services/brand.service';
import { SettingsService } from 'src/app/modules/core/services/settings.service';

import { AppFacade } from 'src/app/modules/ngrx-store/app/app.facade';

import { InteractionContext } from 'src/app/modules/shared/constants/adobe-analytics-fields';
import { IfViewSizeDirective } from 'src/app/modules/shared/directives/if-view-size.directive';
import { getCtaClickProps, getPageLoadEvars } from 'src/app/modules/shared/utilities/adobe-analytics.utils';
import { getPageNameFromUrl } from 'src/app/modules/shared/utilities/page-name.utils';
import { UnsubscribeOnDestroy } from 'src/app/modules/shared/utilities/unsubscribe-on-destroy';

import { IEnvironment } from 'src/environments/environment.model';

@Component({
	selector: 'cp-footer',
	templateUrl: './footer.component.html',
	styleUrls: ['./footer.component.scss'],
	imports: [IfViewSizeDirective, MatIcon, AsyncPipe]
})
export class FooterComponent extends UnsubscribeOnDestroy {
	disclaimers$: Observable<string[]> = this.appFacade.disclaimers$;

	currentYear: number = new Date().getFullYear();

	privacyPolicyUrl = '';

	termsOfUseUrl = '';

	accessibilityStatementUrl = '';

	pageName: string = '';

	private settings?: IEnvironment;

	constructor(
		private appFacade: AppFacade,
		private brandService: BrandService,
		private settingsService: SettingsService,
		private adobeAnalyticsService: AdobeAnalyticsService,
		private router: Router
	) {
		super();

		this.settingsService.getSettings().pipe(
			this.takeUntilDestroyed()
		).subscribe(settings => {
			this.settings = settings;

			const brand = this.brandService.getBrand();
			this.updateFooterLinksForBrand(brand.name);
		});

		// Keep footer links in sync when the brand changes (e.g. realtor switching between client cards)
		this.brandService.currentBrand$.pipe(
			this.takeUntilDestroyed()
		).subscribe(brand => {
			if (this.settings) {
				this.updateFooterLinksForBrand(brand.name);
			}
		});

		// Set pageName on initial load
		this.pageName = getPageNameFromUrl(this.router.url);

		// Listen to route changes and set pageName
		this.router.events.pipe(
			this.takeUntilDestroyed(),
			filter(event => event instanceof NavigationEnd)
		).subscribe((event: NavigationEnd) => {
			this.pageName = getPageNameFromUrl(event.urlAfterRedirects);
		});
	}

	onFooterLinkClick(label: string): void {
		// Track adobe CTA click for footer links and icons
		const evar = getPageLoadEvars(this.pageName);
		const prop = getCtaClickProps(InteractionContext.Navigating, 'Footer', label);
		this.adobeAnalyticsService.trackCtaClick(evar, prop);
	}

	// eslint-disable-next-line class-methods-use-this
	private injectAudioEyeScript(siteHash: string | undefined): void {
		// Always remove all previously injected AudioEye scripts first (e.g. on brand switch)
		document.querySelectorAll('script[data-audioeye]').forEach(el => el.remove());

		if (!siteHash) {
			// Clear the global so a stale hash isn't used if a new script is injected later
			// eslint-disable-next-line no-underscore-dangle
			delete (window as Window & { __AudioEyeSiteHash?: string }).__AudioEyeSiteHash;
			return;
		}

		// Set the global hash that AudioEye reads, then inject the external script directly.
		// Angular is always bootstrapped before the footer renders, so readyState is already
		// 'complete' — the IIFE's load-event dance is unnecessary and leaves an untagged aem.js
		// in the DOM that cannot be cleaned up on subsequent brand switches.
		// eslint-disable-next-line no-underscore-dangle
		(window as Window & { __AudioEyeSiteHash?: string }).__AudioEyeSiteHash = siteHash;

		const script = document.createElement('script');
		script.src = `https://wsv3cdn.audioeye-services.com/aem.js?h=${siteHash}`;
		script.type = 'text/javascript';
		script.crossOrigin = 'anonymous';
		script.async = true;
		script.setAttribute('data-audioeye', '');
		document.body.appendChild(script);
	}

	private updateFooterLinksForBrand(brandName: string): void {
		if (!this.settings) {
			return;
		}

		const brandConfig = this.settings.brandConfigs[brandName];
		if (!brandConfig) {
			return;
		}

		this.privacyPolicyUrl = brandConfig.privacyPolicyUrl;
		this.termsOfUseUrl = brandConfig.termsOfUseUrl;

		const siteHash = brandConfig.audioEyeSiteHash ?? '';
		this.accessibilityStatementUrl = siteHash ? `https://accessibility-statement.audioeye-services.com/?siteHash=${siteHash}` : '';
		this.injectAudioEyeScript(siteHash);
	}
}
